// =============================================================================
// LP Express — "Cold Drip Offer (Gaffle)" template renderer.
//
// Renders a `generatedContent.coldDrip` blob (see shared/lp-express-cold-drip)
// as the imported Gaffle Cold Drip Bundle offer page: promo banner → sticky
// header → hero + buy box → marquee ticker → meet-the-duo product deep-dive →
// bundle benefits → why-Gaffle science → gut-problem stats → comparison table
// → inline buy CTA → ingredients → community wall → join-the-fam → reviews →
// who-we-are → visit/location → CTA strip → FAQ → footer → sticky bottom bar.
//
//  • Copy is fully editable via the shared path-based `setText` ops (paths
//    like ["coldDrip","hero","headline"]); CTA URLs edit via the shared link
//    popover on ["coldDrip","pricing","checkoutUrl"].
//  • Sections reorder/hide/delete through ONE page-level LpReorderGroup +
//    per-section EditableElement (componentOrder + element state persist and
//    are honored on the published page).
//  • Imagery: the logo, buy-box bundle photo, and per-product duo cards show
//    REAL brand/product assets (fallbackUrl); the ingredient close-ups,
//    community wall, and who-we-are story photos are AI-GENERATED per-slot
//    (generatedContent.coldDripImages — same model as the listicle; per-slot
//    spinner/failed states + retry, LP_COLD_DRIP_IMAGE_SLOTS). Every slot
//    stays overridable via the media picker/upload (mediaOverrides win).
//  • Per-section copy regen anchors on each section heading with dynamic block
//    keys `cold-drip-<sectionKey>` (see regenerate-cold-drip-section).
//  • Pricing/savings render from the blob's pricing config ({savings}/
//    {percent} placeholders in savingsLine); copy itself carries the export's
//    literal offer framing (verbatim import).
// =============================================================================
import {
  BadgeCheck, CheckCircle2, Clock, ExternalLink, ImageIcon, Loader2, MapPin,
  Minus, Phone, RotateCw, ShoppingBag, Star, X,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  LpReorderGroup,
  LpImageActions,
  LinkUrlPopover,
  useLpEditor,
  useLpMediaOverride,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import {
  normalizeLpColdDripContent,
  LP_COLD_DRIP_SECTIONS,
  LP_COLD_DRIP_IMAGE_SLOTS,
  type LpColdDripContent,
} from "@shared/lp-express-cold-drip";

// AI-generatable slot keys (ingredient-0..3, community-0..7, story, skincare).
// The copy workflow can return MORE ingredient cards than we define slots for;
// cards past the last defined slot fall back to the plain upload/media-picker
// placeholder instead of wiring a retry that the server would reject.
const COLD_DRIP_AI_SLOT_KEYS = new Set(LP_COLD_DRIP_IMAGE_SLOTS.map((s) => s.key));

export type ColdDripCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  // First photo per page product (index-aligned with the duo cards).
  perProductPhotos?: (string | null)[];
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** Retry a single failed AI image slot (coldDripImages). In-app builder
   *  only — the public/published page leaves it undefined so failed slots
   *  stay as static placeholders. */
  onRetrySlot?: (mergeKey: "coldDripImages", slotKey: string) => void;
};

// ── Palette ───────────────────────────────────────────────────────────────
// The template's color roles resolve to the BRAND's palette via the same
// `--color-brand-*` CSS variables the v2/listicle/advertorial templates use
// (brandCssVarsV2 in lp-express-renderer.tsx applies them, with luminance
// gates + neutral fallbacks, at the renderer root). The var() fallbacks below
// are the original Gaffle export hexes, so the page still renders sensibly if
// the vars are ever missing. MUTED/BORDER stay neutral grays (not branded).
const RED = "var(--color-brand-coral, #AE1F2E)";
const TEAL = "var(--color-brand-navy, #0BB6D5)";
const WINE = "var(--color-brand-deepnavy, #600A2C)";
const CREAM = "var(--color-brand-cream, #FDF4F0)";
const INK = "var(--color-brand-text, #1D1D1F)";
const GOLD = "var(--color-brand-goldaccent, #E8A33D)";
const MUTED = "#6B6B6B";
const BORDER = "rgba(29,29,31,0.12)";

// "Oaxaca Prickly" is served via @font-face in index.css (licensed binary at
// client/public/fonts/OaxacaPrickly.otf); Georgia is the load-failure fallback.
const DISPLAY_FONT = '"Oaxaca Prickly", Georgia, "Times New Roman", serif';

const CHECKOUT_PATH: (string | number)[] = ["coldDrip", "pricing", "checkoutUrl"];

// ── Media slots (picker/upload only — no AI pipeline) ────────────────────────

function coldDripMediaKey(slot: string): string {
  return `cold-drip:img:${slot}`;
}

function MediaSlot({
  slot,
  label,
  className,
  style,
  testId,
  fallbackUrl,
  aiSlot,
  onRegenerate,
}: {
  slot: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  testId: string;
  // Real brand/product asset shown until the user overrides the slot via the
  // media picker/upload (mirrors the product-photo slots on other templates).
  fallbackUrl?: string | null;
  // AI-generated slot record from generatedContent.coldDripImages (keyed by
  // `slot`). Priority: media override > ready AI image > fallbackUrl. While
  // the slot is pending a spinner shows; on failure the builder gets a retry
  // affordance (the published page shows a static placeholder).
  aiSlot?: { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null } | null;
  // Re-fires the single-slot AI pipeline (retry on failure, regenerate when
  // ready). Builder only — undefined on the published surface.
  onRegenerate?: () => void;
}) {
  const overrideUrl = useLpMediaOverride(coldDripMediaKey(slot));
  const editor = useLpEditor();
  const actions = (
    <LpImageActions
      slotKey={coldDripMediaKey(slot)}
      canRegenerate={!!onRegenerate}
      onRegenerate={onRegenerate}
      testIdPrefix={testId}
    />
  );
  const aiUrl =
    aiSlot?.imageStatus === "ready" && aiSlot.imageUrl ? aiSlot.imageUrl : null;
  const url = overrideUrl || aiUrl || fallbackUrl || null;
  if (url) {
    return (
      <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
        <img
          src={url}
          alt={label || ""}
          loading="lazy"
          className={className}
          style={{ objectFit: "cover", display: "block", width: "100%", height: "100%", ...style }}
          data-testid={testId}
        />
        {actions}
      </div>
    );
  }
  return (
    <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
      <div
        className={className}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 6, width: "100%", height: "100%", minHeight: 120, backgroundColor: CREAM,
          border: `1px dashed ${BORDER}`, borderRadius: 12, ...style,
        }}
        data-testid={testId}
      >
        {aiSlot?.imageStatus === "pending" ? (
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
        ) : aiSlot?.imageStatus === "failed" && editor && onRegenerate ? (
          <button
            type="button"
            onClick={onRegenerate}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
            style={{ color: MUTED, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
            data-testid={`${testId}-retry`}
          >
            <RotateCw className="h-3 w-3" /> Retry image
          </button>
        ) : (
          <ImageIcon className="h-5 w-5" style={{ color: MUTED }} />
        )}
        {editor && aiSlot?.imageStatus !== "pending" && (
          <span style={{ fontSize: 11, color: MUTED, textAlign: "center", padding: "0 8px" }}>
            {aiSlot?.imageStatus === "failed"
              ? "Image generation failed"
              : label || "Upload or pick an image"}
          </span>
        )}
      </div>
      {actions}
    </div>
  );
}

// ── CTA button (editable label + shared URL popover on the checkout path) ────

function Cta({
  label,
  path,
  href,
  testId,
  variant = "red",
  block = false,
}: {
  label: string;
  path: (string | number)[];
  href: string;
  testId: string;
  variant?: "red" | "teal" | "outline";
  block?: boolean;
}) {
  const editor = useLpEditor();
  const style: React.CSSProperties = {
    display: block ? "block" : "inline-block",
    width: block ? "100%" : undefined,
    padding: block ? "15px 0" : "13px 28px",
    backgroundColor: variant === "red" ? RED : variant === "teal" ? TEAL : "transparent",
    color: variant === "outline" ? INK : "#FFFFFF",
    border: variant === "outline" ? `2px solid ${INK}` : "none",
    fontSize: 15,
    fontWeight: 800,
    textAlign: "center",
    borderRadius: 999,
    letterSpacing: "0.02em",
    textDecoration: "none",
    boxShadow: variant === "outline" ? "none" : "0 4px 14px rgba(0,0,0,0.16)",
    cursor: "pointer",
  };
  const inner = (
    <EditableText value={label} path={path} as="span" noHideDelete placeholder="Try It Now" testId={`${testId}-label`} />
  );
  if (!editor) {
    return (
      <a href={href || undefined} style={style} data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <span className="group/lplink relative" style={{ display: block ? "block" : "inline-block", width: block ? "100%" : undefined }}>
      <span style={style} data-testid={testId}>
        {inner}
      </span>
      {editor.onEditLinkUrl && <LinkUrlPopover path={CHECKOUT_PATH} url={href} testId={testId} />}
    </span>
  );
}

// Section shell: EditableElement wrapper + regen anchoring is done per-heading.
function Shell({
  k,
  children,
  style,
}: {
  k: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const editor = useLpEditor();
  return (
    <EditableElement elementKey={`cold-drip-${k}`} toolbarSide="left">
      <section style={style} data-testid={`cold-drip-section-${k}`}>
        {children}
      </section>
    </EditableElement>
  );
}

const wrap: React.CSSProperties = { maxWidth: 1080, margin: "0 auto", padding: "0 20px" };

function Eyebrow({ value, path, testId }: { value: string; path: (string | number)[]; testId: string }) {
  return (
    <EditableText
      value={value}
      path={path}
      as="div"
      style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: TEAL, marginBottom: 10 }}
      placeholder="Eyebrow"
      testId={testId}
    />
  );
}

function Heading({
  value, path, testId, regenKey, color = INK, size = 34,
}: {
  value: string; path: (string | number)[]; testId: string; regenKey: string; color?: string; size?: number;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      style={{ fontFamily: DISPLAY_FONT, fontSize: size, lineHeight: 1.15, fontWeight: 800, color, margin: "0 0 14px" }}
      placeholder="Heading"
      testId={testId}
      regenBlockKey={regenKey}
      regenAnchor
    />
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function LpExpressColdDrip({ ctx }: { ctx: ColdDripCtx }) {
  const cd: LpColdDripContent = normalizeLpColdDripContent(ctx.generatedContent?.coldDrip);
  const editor = useLpEditor();

  const checkoutUrl = cd.pricing.checkoutUrl || ctx.offer?.checkoutUrl || "";
  const price = cd.pricing.bundlePriceOneTime;
  const full = cd.pricing.bundleFullPrice;
  const savings = Math.max(0, full - price);
  const percent = full > 0 ? Math.round((savings / full) * 100) : 0;
  const money = (n: number) => `$${n.toFixed(2)}`;
  const savingsLine = cd.buyBox.savingsLine
    .replace("{savings}", money(savings))
    .replace("{percent}", String(percent));
  const rk = (key: string) => `cold-drip-${key}`;
  const P = (...p: (string | number)[]) => ["coldDrip", ...p];

  // ── AI image slots (generatedContent.coldDripImages, keyed by slot key) ───
  // Ingredient close-ups, community wall tiles, and the two who-we-are story
  // photos come from the per-slot AI pipeline; everything else stays real
  // brand/product assets. Media overrides always win inside MediaSlot.
  const aiSlots: Record<string, { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null }> = {};
  for (const s of (ctx.generatedContent?.coldDripImages ?? []) as any[]) {
    if (s?.key) aiSlots[s.key] = s;
  }
  const retrySlot = ctx.onRetrySlot
    ? (slotKey: string) => () => ctx.onRetrySlot!("coldDripImages", slotKey)
    : (_slotKey: string) => undefined;

  // ── Per-section renderers (keyed to LP_COLD_DRIP_SECTIONS) ────────────────
  const renderers: Record<string, () => React.ReactNode> = {
    promoBanner: () => (
      <Shell k="promoBanner" style={{ backgroundColor: RED }}>
        <EditableText
          value={cd.promoBanner}
          path={P("promoBanner")}
          as="div"
          style={{ ...wrap, padding: "9px 20px", color: "#fff", fontSize: 12.5, fontWeight: 800, letterSpacing: "0.1em", textAlign: "center", textTransform: "uppercase" }}
          placeholder="Promo banner"
          testId="text-cold-drip-promo"
          regenBlockKey={rk("promoBanner")}
          regenAnchor
        />
      </Shell>
    ),
    header: () => (
      <Shell k="header" style={{ backgroundColor: CREAM, borderBottom: `1px solid ${BORDER}`, position: "sticky", top: 0, zIndex: 30 }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
          <div style={{ width: 110, height: 34 }}>
            <MediaSlot slot="logo" label="Logo" testId="img-cold-drip-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 34, borderRadius: 6 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <EditableText value={cd.header.shopPill} path={P("header", "shopPill")} as="span" style={{ fontSize: 13, fontWeight: 800, backgroundColor: INK, color: "#fff", borderRadius: 999, padding: "7px 18px" }} placeholder="Shop" testId="text-cold-drip-header-shop" regenBlockKey={rk("header")} regenAnchor />
            <EditableText value={cd.header.signIn} path={P("header", "signIn")} as="span" style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: INK }} placeholder="SIGN IN" testId="text-cold-drip-header-signin" regenBlockKey={rk("header")} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: INK }}>
              <ShoppingBag className="h-4 w-4" />
              <EditableText value={cd.header.bag} path={P("header", "bag")} as="span" placeholder="BAG" testId="text-cold-drip-header-bag" regenBlockKey={rk("header")} />
            </span>
          </div>
        </div>
      </Shell>
    ),
    hero: () => (
      <Shell k="hero" style={{ backgroundColor: CREAM, padding: "44px 0 56px" }}>
        <div style={{ ...wrap, display: "grid", gridTemplateColumns: "1fr", gap: 32 }} className="lg:!grid-cols-[1.15fr_0.85fr]">
          <div>
            <EditableText value={cd.hero.badge} path={P("hero", "badge")} as="div" style={{ display: "inline-block", fontSize: 12, fontWeight: 800, color: WINE, backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 999, padding: "6px 14px", marginBottom: 16 }} placeholder="Badge" testId="text-cold-drip-hero-badge" regenBlockKey={rk("hero")} />
            <EditableText value={cd.hero.headline} path={P("hero", "headline")} as="h1" style={{ fontFamily: DISPLAY_FONT, fontSize: 52, lineHeight: 1.05, fontWeight: 900, color: INK, margin: "0 0 10px" }} placeholder="Headline" testId="text-cold-drip-hero-headline" regenBlockKey={rk("hero")} regenAnchor />
            <EditableText value={cd.hero.subheadline} path={P("hero", "subheadline")} as="p" style={{ fontSize: 20, fontWeight: 700, color: WINE, margin: "0 0 12px" }} placeholder="Subheadline" testId="text-cold-drip-hero-sub" regenBlockKey={rk("hero")} />
            <EditableText value={cd.hero.offerPill} path={P("hero", "offerPill")} as="div" style={{ display: "inline-block", fontSize: 13, fontWeight: 800, color: "#fff", backgroundColor: TEAL, borderRadius: 999, padding: "7px 16px", marginBottom: 16 }} placeholder="Offer pill" testId="text-cold-drip-hero-offer" regenBlockKey={rk("hero")} />
            <EditableText value={cd.hero.body} path={P("hero", "body")} as="p" multiline style={{ fontSize: 16, lineHeight: 1.65, color: INK, margin: "0 0 16px", maxWidth: 560 }} placeholder="Body" testId="text-cold-drip-hero-body" regenBlockKey={rk("hero")} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: INK }}>
                <Star className="h-4 w-4" style={{ color: GOLD, fill: GOLD }} />
                <EditableText value={cd.hero.reviewStat} path={P("hero", "reviewStat")} as="span" placeholder="Reviews" testId="text-cold-drip-hero-reviews" regenBlockKey={rk("hero")} />
              </span>
              <EditableText value={cd.hero.cupsStat} path={P("hero", "cupsStat")} as="span" style={{ fontSize: 13, fontWeight: 700, color: MUTED }} placeholder="Cups stat" testId="text-cold-drip-hero-cups" regenBlockKey={rk("hero")} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {cd.hero.benefitChips.map((c, i) => (
                <EditableText key={i} value={c} path={P("hero", "benefitChips", i)} as="span" style={{ fontSize: 12.5, fontWeight: 700, color: WINE, backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 999, padding: "6px 12px" }} placeholder="Chip" testId={`text-cold-drip-hero-chip-${i}`} regenBlockKey={rk("hero")} />
              ))}
            </div>
            <EditableText value={cd.hero.tagline} path={P("hero", "tagline")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 22, fontWeight: 800, color: TEAL, marginBottom: 10 }} placeholder="Tagline" testId="text-cold-drip-hero-tagline" regenBlockKey={rk("hero")} />
            <EditableText value={cd.hero.guarantee} path={P("hero", "guarantee")} as="div" style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.12em", color: MUTED }} placeholder="Guarantee" testId="text-cold-drip-hero-guarantee" regenBlockKey={rk("hero")} />
          </div>
          {/* Buy box */}
          <div style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 20, padding: 22, boxShadow: "0 12px 32px rgba(0,0,0,0.10)", alignSelf: "start" }} data-testid="cold-drip-buy-box">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <EditableText value={cd.buyBox.badge} path={P("buyBox", "badge")} as="span" style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: WINE }} placeholder="Badge" testId="text-cold-drip-buybox-badge" regenBlockKey={rk("hero")} />
              <EditableText value={cd.buyBox.savePill} path={P("buyBox", "savePill")} as="span" style={{ fontSize: 11.5, fontWeight: 800, color: "#fff", backgroundColor: RED, borderRadius: 999, padding: "4px 10px" }} placeholder="Save pill" testId="text-cold-drip-buybox-save" regenBlockKey={rk("hero")} />
            </div>
            <div style={{ borderRadius: 14, overflow: "hidden", marginBottom: 14, height: 200 }}>
              <MediaSlot slot="bundle" label="Bundle photo (both bottles)" testId="img-cold-drip-bundle" fallbackUrl={ctx.bundleImage || ctx.bundleProductPhotos[0] || null} />
            </div>
            <EditableText value={cd.buyBox.title} path={P("buyBox", "title")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 24, fontWeight: 800, color: INK, margin: "0 0 4px" }} placeholder="Bundle title" testId="text-cold-drip-buybox-title" regenBlockKey={rk("hero")} />
            <EditableText value={cd.buyBox.subtitle} path={P("buyBox", "subtitle")} as="p" style={{ fontSize: 13.5, color: MUTED, margin: "0 0 12px" }} placeholder="Subtitle" testId="text-cold-drip-buybox-subtitle" regenBlockKey={rk("hero")} />
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 30, fontWeight: 900, color: INK }} data-testid="text-cold-drip-price">{money(price)}</span>
              {full > price && (
                <span style={{ fontSize: 17, fontWeight: 600, color: MUTED, textDecoration: "line-through" }} data-testid="text-cold-drip-full-price">{money(full)}</span>
              )}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: RED, marginBottom: 14 }} data-testid="text-cold-drip-savings">{savingsLine}</div>
            <div style={{ marginBottom: 10 }}>
              <Cta label={cd.buyBox.ctaPrimary} path={P("buyBox", "ctaPrimary")} href={checkoutUrl} testId="button-cold-drip-buybox-primary" block />
            </div>
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <Cta label={cd.buyBox.ctaSecondary} path={P("buyBox", "ctaSecondary")} href={checkoutUrl} testId="button-cold-drip-buybox-secondary" variant="outline" block />
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              {cd.buyBox.trustBadges.map((t, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: MUTED }}>
                  <BadgeCheck className="h-3.5 w-3.5" style={{ color: TEAL }} />
                  <EditableText value={t} path={P("buyBox", "trustBadges", i)} as="span" placeholder="Trust badge" testId={`text-cold-drip-trust-${i}`} regenBlockKey={rk("hero")} />
                </span>
              ))}
            </div>
          </div>
        </div>
      </Shell>
    ),
    marquee: () => (
      <Shell k="marquee" style={{ backgroundColor: WINE, overflow: "hidden" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px 28px", padding: "12px 16px" }}>
          {cd.marquee.map((m, i) => (
            <EditableText key={i} value={m} path={P("marquee", i)} as="span" style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: "0.1em", color: "#fff", whiteSpace: "nowrap" }} placeholder="Ticker item" testId={`text-cold-drip-marquee-${i}`} regenBlockKey={rk("marquee")} regenAnchor={i === 0} />
          ))}
        </div>
      </Shell>
    ),
    meetTheDuo: () => (
      <Shell k="meetTheDuo" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 36px" }}>
            <Eyebrow value={cd.meetTheDuo.eyebrow} path={P("meetTheDuo", "eyebrow")} testId="text-cold-drip-duo-eyebrow" />
            <Heading value={cd.meetTheDuo.heading} path={P("meetTheDuo", "heading")} testId="text-cold-drip-duo-heading" regenKey={rk("meetTheDuo")} size={38} />
            <EditableText value={cd.meetTheDuo.body} path={P("meetTheDuo", "body")} as="p" multiline style={{ fontSize: 15.5, lineHeight: 1.65, color: MUTED, margin: "0 0 14px" }} placeholder="Body" testId="text-cold-drip-duo-body" regenBlockKey={rk("meetTheDuo")} />
            <EditableText value={cd.meetTheDuo.pill} path={P("meetTheDuo", "pill")} as="div" style={{ display: "inline-block", fontSize: 12.5, fontWeight: 800, color: WINE, backgroundColor: CREAM, borderRadius: 999, padding: "7px 16px" }} placeholder="Pill" testId="text-cold-drip-duo-pill" regenBlockKey={rk("meetTheDuo")} />
          </div>
          {/* The reference template always shows exactly two products side by
              side ("meet the duo"). With a single page product the lone card
              centers at duo-card width instead of hugging the left column. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 24,
              ...(cd.meetTheDuo.products.length === 1
                ? { maxWidth: 480, margin: "0 auto" }
                : {}),
            }}
            className={cd.meetTheDuo.products.length > 1 ? "md:!grid-cols-2" : undefined}
          >
            {cd.meetTheDuo.products.map((p, i) => (
              <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 20, overflow: "hidden", backgroundColor: CREAM }} data-testid={`card-cold-drip-product-${i}`}>
                <div style={{ height: 240 }}>
                  <MediaSlot slot={`product-${i}`} label={`${p.title || "Product"} photo`} testId={`img-cold-drip-product-${i}`} fallbackUrl={ctx.perProductPhotos?.[i] || ctx.bundleProductPhotos[i] || null} style={{ borderRadius: 0 }} />
                </div>
                <div style={{ padding: 22 }}>
                  <EditableText value={p.caffeine} path={P("meetTheDuo", "products", i, "caffeine")} as="div" style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, marginBottom: 6 }} placeholder="Caffeine" testId={`text-cold-drip-product-caffeine-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  <EditableText value={p.title} path={P("meetTheDuo", "products", i, "title")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 24, fontWeight: 800, color: INK, margin: "0 0 4px" }} placeholder="Product title" testId={`text-cold-drip-product-title-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  <EditableText value={p.kicker} path={P("meetTheDuo", "products", i, "kicker")} as="p" style={{ fontSize: 14, fontWeight: 700, color: WINE, margin: "0 0 6px" }} placeholder="Kicker" testId={`text-cold-drip-product-kicker-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  <EditableText value={p.tasting} path={P("meetTheDuo", "products", i, "tasting")} as="div" style={{ fontSize: 12.5, fontStyle: "italic", color: MUTED, marginBottom: 10 }} placeholder="Tasting notes" testId={`text-cold-drip-product-tasting-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  <EditableText value={p.blurb} path={P("meetTheDuo", "products", i, "blurb")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: INK, margin: "0 0 12px" }} placeholder="Blurb" testId={`text-cold-drip-product-blurb-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12, marginBottom: 12 }}>
                    {p.properties.map((prop, pi) => (
                      <div key={pi} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", fontSize: 13 }}>
                        <EditableText value={prop.label} path={P("meetTheDuo", "products", i, "properties", pi, "label")} as="span" style={{ fontWeight: 700, color: MUTED }} placeholder="Label" testId={`text-cold-drip-prop-label-${i}-${pi}`} regenBlockKey={rk("meetTheDuo")} />
                        <EditableText value={prop.value} path={P("meetTheDuo", "products", i, "properties", pi, "value")} as="span" style={{ fontWeight: 600, color: INK, textAlign: "right" }} placeholder="Value" testId={`text-cold-drip-prop-value-${i}-${pi}`} regenBlockKey={rk("meetTheDuo")} />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 700, color: INK }}>
                      <Star className="h-3.5 w-3.5" style={{ color: GOLD, fill: GOLD }} />
                      <EditableText value={p.reviews} path={P("meetTheDuo", "products", i, "reviews")} as="span" placeholder="Reviews" testId={`text-cold-drip-product-reviews-${i}`} regenBlockKey={rk("meetTheDuo")} />
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>
                    <EditableText value={p.bottomNote} path={P("meetTheDuo", "products", i, "bottomNote")} as="span" placeholder="Note" testId={`text-cold-drip-product-note-${i}`} regenBlockKey={rk("meetTheDuo")} />{" "}
                    <EditableText value={p.bottomNoteEmphasis} path={P("meetTheDuo", "products", i, "bottomNoteEmphasis")} as="span" style={{ fontStyle: "italic", fontWeight: 700 }} placeholder="Emphasis" testId={`text-cold-drip-product-note-em-${i}`} regenBlockKey={rk("meetTheDuo")} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 28, backgroundColor: WINE, borderRadius: 20, padding: "30px 26px", textAlign: "center" }}>
            <EditableText value={cd.meetTheDuo.sharedCta.eyebrow} path={P("meetTheDuo", "sharedCta", "eyebrow")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: TEAL, marginBottom: 8 }} placeholder="Eyebrow" testId="text-cold-drip-duo-cta-eyebrow" regenBlockKey={rk("meetTheDuo")} />
            <EditableText value={cd.meetTheDuo.sharedCta.heading} path={P("meetTheDuo", "sharedCta", "heading")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 28, fontWeight: 800, color: "#fff", margin: "0 0 8px" }} placeholder="Heading" testId="text-cold-drip-duo-cta-heading" regenBlockKey={rk("meetTheDuo")} />
            <EditableText value={cd.meetTheDuo.sharedCta.body} path={P("meetTheDuo", "sharedCta", "body")} as="p" multiline style={{ fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)", margin: "0 auto 16px", maxWidth: 560 }} placeholder="Body" testId="text-cold-drip-duo-cta-body" regenBlockKey={rk("meetTheDuo")} />
            <Cta label={cd.buyBox.ctaPrimary} path={P("buyBox", "ctaPrimary")} href={checkoutUrl} testId="button-cold-drip-duo-cta" variant="teal" />
          </div>
        </div>
      </Shell>
    ),
    bundleBenefits: () => (
      <Shell k="bundleBenefits" style={{ backgroundColor: CREAM, padding: "60px 0" }}>
        <div style={{ ...wrap, textAlign: "center" }}>
          <EditableText value={cd.bundleBenefits.badge} path={P("bundleBenefits", "badge")} as="div" style={{ display: "inline-block", fontSize: 12, fontWeight: 800, color: "#fff", backgroundColor: RED, borderRadius: 999, padding: "6px 16px", marginBottom: 14 }} placeholder="Badge" testId="text-cold-drip-benefits-badge" regenBlockKey={rk("bundleBenefits")} />
          <Heading value={cd.bundleBenefits.heading} path={P("bundleBenefits", "heading")} testId="text-cold-drip-benefits-heading" regenKey={rk("bundleBenefits")} size={36} />
          <EditableText value={cd.bundleBenefits.intro} path={P("bundleBenefits", "intro")} as="p" style={{ fontSize: 15.5, color: MUTED, margin: "0 auto 26px", maxWidth: 560 }} placeholder="Intro" testId="text-cold-drip-benefits-intro" regenBlockKey={rk("bundleBenefits")} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18, marginBottom: 24, textAlign: "left" }} className="md:!grid-cols-2">
            {cd.bundleBenefits.cards.map((c, i) => (
              <div key={i} style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: 22 }} data-testid={`card-cold-drip-benefit-${i}`}>
                <EditableText value={c.tag} path={P("bundleBenefits", "cards", i, "tag")} as="div" style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", color: TEAL, marginBottom: 8 }} placeholder="Tag" testId={`text-cold-drip-benefit-tag-${i}`} regenBlockKey={rk("bundleBenefits")} />
                <EditableText value={c.body} path={P("bundleBenefits", "cards", i, "body")} as="p" multiline style={{ fontSize: 14.5, lineHeight: 1.65, color: INK, margin: 0 }} placeholder="Body" testId={`text-cold-drip-benefit-body-${i}`} regenBlockKey={rk("bundleBenefits")} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 14 }}>
            <Cta label={cd.bundleBenefits.cta} path={P("bundleBenefits", "cta")} href={checkoutUrl} testId="button-cold-drip-benefits-cta" />
          </div>
          <EditableText value={cd.bundleBenefits.ingredientLine} path={P("bundleBenefits", "ingredientLine")} as="p" style={{ fontSize: 12.5, color: MUTED }} placeholder="Ingredient line" testId="text-cold-drip-benefits-ingredients" regenBlockKey={rk("bundleBenefits")} />
        </div>
      </Shell>
    ),
    whyGaffle: () => (
      <Shell k="whyGaffle" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 34px" }}>
            <Eyebrow value={cd.whyGaffle.eyebrow} path={P("whyGaffle", "eyebrow")} testId="text-cold-drip-why-eyebrow" />
            <Heading value={cd.whyGaffle.heading} path={P("whyGaffle", "heading")} testId="text-cold-drip-why-heading" regenKey={rk("whyGaffle")} size={36} />
            <EditableText value={cd.whyGaffle.intro} path={P("whyGaffle", "intro")} as="p" style={{ fontSize: 15, color: MUTED, margin: 0 }} placeholder="Intro" testId="text-cold-drip-why-intro" regenBlockKey={rk("whyGaffle")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }} className="md:!grid-cols-2">
            {cd.whyGaffle.benefits.map((b, i) => (
              <div key={i} style={{ backgroundColor: CREAM, borderRadius: 16, padding: 22 }} data-testid={`card-cold-drip-why-${i}`}>
                <EditableText value={b.tag} path={P("whyGaffle", "benefits", i, "tag")} as="div" style={{ fontSize: 11.5, fontWeight: 900, letterSpacing: "0.12em", color: RED, marginBottom: 6 }} placeholder="Tag" testId={`text-cold-drip-why-tag-${i}`} regenBlockKey={rk("whyGaffle")} />
                <EditableText value={b.title} path={P("whyGaffle", "benefits", i, "title")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 20, fontWeight: 800, color: INK, margin: "0 0 8px" }} placeholder="Title" testId={`text-cold-drip-why-title-${i}`} regenBlockKey={rk("whyGaffle")} />
                <EditableText value={b.body} path={P("whyGaffle", "benefits", i, "body")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: INK, margin: "0 0 10px" }} placeholder="Body" testId={`text-cold-drip-why-body-${i}`} regenBlockKey={rk("whyGaffle")} />
                <div style={{ fontSize: 12, color: MUTED }}>
                  {editor ? (
                    <span className="group/lplink relative inline-flex" style={{ gap: 4, alignItems: "center" }}>
                      <EditableText value={b.sourceLabel} path={P("whyGaffle", "benefits", i, "sourceLabel")} as="span" style={{ textDecoration: "underline" }} placeholder="Source" testId={`text-cold-drip-why-source-${i}`} regenBlockKey={rk("whyGaffle")} />
                      {editor.onEditLinkUrl && (
                        <LinkUrlPopover path={P("whyGaffle", "benefits", i, "sourceUrl")} url={b.sourceUrl} testId={`link-cold-drip-why-source-${i}`} />
                      )}
                    </span>
                  ) : (
                    <a href={b.sourceUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: MUTED, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`link-cold-drip-why-source-${i}`}>
                      {b.sourceLabel} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    gutProblem: () => (
      <Shell k="gutProblem" style={{ backgroundColor: WINE, padding: "60px 0", color: "#fff" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 30px" }}>
            <EditableText value={cd.gutProblem.eyebrow} path={P("gutProblem", "eyebrow")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: TEAL, marginBottom: 10 }} placeholder="Eyebrow" testId="text-cold-drip-gut-eyebrow" regenBlockKey={rk("gutProblem")} />
            <EditableText value={cd.gutProblem.heading} path={P("gutProblem", "heading")} as="h2" style={{ fontFamily: DISPLAY_FONT, fontSize: 36, fontWeight: 800, color: "#fff", margin: "0 0 12px" }} placeholder="Heading" testId="text-cold-drip-gut-heading" regenBlockKey={rk("gutProblem")} regenAnchor />
            <EditableText value={cd.gutProblem.intro} path={P("gutProblem", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.65, color: "rgba(255,255,255,0.85)", margin: 0 }} placeholder="Intro" testId="text-cold-drip-gut-intro" regenBlockKey={rk("gutProblem")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 24 }} className="md:!grid-cols-3">
            {cd.gutProblem.stats.map((s, i) => (
              <div key={i} style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 20, textAlign: "center" }} data-testid={`card-cold-drip-gut-stat-${i}`}>
                <EditableText value={s.stat} path={P("gutProblem", "stats", i, "stat")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 34, fontWeight: 900, color: TEAL, marginBottom: 6 }} placeholder="Stat" testId={`text-cold-drip-gut-stat-${i}`} regenBlockKey={rk("gutProblem")} />
                <EditableText value={s.label} path={P("gutProblem", "stats", i, "label")} as="p" multiline style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.8)", margin: 0 }} placeholder="Label" testId={`text-cold-drip-gut-stat-label-${i}`} regenBlockKey={rk("gutProblem")} />
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 26 }} className="md:!grid-cols-3">
            {cd.gutProblem.cards.map((c, i) => (
              <div key={i} style={{ backgroundColor: "#fff", borderRadius: 14, padding: 20, color: INK }} data-testid={`card-cold-drip-gut-${i}`}>
                <EditableText value={c.title} path={P("gutProblem", "cards", i, "title")} as="h3" style={{ fontSize: 16.5, fontWeight: 800, color: WINE, margin: "0 0 8px" }} placeholder="Title" testId={`text-cold-drip-gut-card-title-${i}`} regenBlockKey={rk("gutProblem")} />
                <EditableText value={c.body} path={P("gutProblem", "cards", i, "body")} as="p" multiline style={{ fontSize: 13.5, lineHeight: 1.6, color: INK, margin: "0 0 10px" }} placeholder="Body" testId={`text-cold-drip-gut-card-body-${i}`} regenBlockKey={rk("gutProblem")} />
                <div style={{ fontSize: 11.5, color: MUTED }}>
                  {editor ? (
                    <span className="group/lplink relative inline-flex" style={{ gap: 4 }}>
                      <EditableText value={c.source} path={P("gutProblem", "cards", i, "source")} as="span" style={{ textDecoration: "underline" }} placeholder="Source" testId={`text-cold-drip-gut-source-${i}`} regenBlockKey={rk("gutProblem")} />
                      {editor.onEditLinkUrl && (
                        <LinkUrlPopover path={P("gutProblem", "cards", i, "href")} url={c.href} testId={`link-cold-drip-gut-source-${i}`} />
                      )}
                    </span>
                  ) : (
                    <a href={c.href || undefined} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: MUTED }} data-testid={`link-cold-drip-gut-source-${i}`}>
                      {c.source}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <EditableText value={cd.gutProblem.bridgeLine} path={P("gutProblem", "bridgeLine")} as="p" style={{ fontFamily: DISPLAY_FONT, fontSize: 19, fontWeight: 700, color: "#fff", margin: "0 0 16px" }} placeholder="Bridge line" testId="text-cold-drip-gut-bridge" regenBlockKey={rk("gutProblem")} />
            <Cta label={cd.gutProblem.cta} path={P("gutProblem", "cta")} href={checkoutUrl} testId="button-cold-drip-gut-cta" variant="teal" />
          </div>
        </div>
      </Shell>
    ),
    comparison: () => (
      <Shell k="comparison" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={{ ...wrap, maxWidth: 860 }}>
          <div style={{ textAlign: "center", marginBottom: 26 }}>
            <Eyebrow value={cd.comparison.eyebrow} path={P("comparison", "eyebrow")} testId="text-cold-drip-cmp-eyebrow" />
            <Heading value={cd.comparison.heading} path={P("comparison", "heading")} testId="text-cold-drip-cmp-heading" regenKey={rk("comparison")} size={34} />
          </div>
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", backgroundColor: CREAM }}>
              <EditableText value={cd.comparison.columns.us} path={P("comparison", "columns", "us")} as="div" style={{ padding: "13px 16px", fontFamily: DISPLAY_FONT, fontSize: 17, fontWeight: 900, color: RED, borderRight: `1px solid ${BORDER}` }} placeholder="Us" testId="text-cold-drip-cmp-col-us" regenBlockKey={rk("comparison")} />
              <EditableText value={cd.comparison.columns.them} path={P("comparison", "columns", "them")} as="div" style={{ padding: "13px 16px", fontSize: 13, fontWeight: 800, letterSpacing: "0.06em", color: MUTED }} placeholder="Them" testId="text-cold-drip-cmp-col-them" regenBlockKey={rk("comparison")} />
            </div>
            {cd.comparison.rows.map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", borderTop: `1px solid ${BORDER}` }} data-testid={`row-cold-drip-cmp-${i}`}>
                <div style={{ padding: "12px 16px", display: "flex", gap: 8, alignItems: "flex-start", borderRight: `1px solid ${BORDER}` }}>
                  <CheckCircle2 className="h-4 w-4" style={{ color: TEAL, flexShrink: 0, marginTop: 2 }} />
                  <EditableText value={r.us} path={P("comparison", "rows", i, "us")} as="span" multiline style={{ fontSize: 13.5, fontWeight: 600, color: INK }} placeholder="Us" testId={`text-cold-drip-cmp-us-${i}`} regenBlockKey={rk("comparison")} />
                </div>
                <div style={{ padding: "12px 16px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <X className="h-4 w-4" style={{ color: "#C33", flexShrink: 0, marginTop: 2 }} />
                  <EditableText value={r.them} path={P("comparison", "rows", i, "them")} as="span" multiline style={{ fontSize: 13.5, color: MUTED }} placeholder="Them" testId={`text-cold-drip-cmp-them-${i}`} regenBlockKey={rk("comparison")} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <EditableText value={cd.comparison.footnote} path={P("comparison", "footnote")} as="p" style={{ fontSize: 13, fontStyle: "italic", color: MUTED, margin: "0 0 16px" }} placeholder="Footnote" testId="text-cold-drip-cmp-footnote" regenBlockKey={rk("comparison")} />
            <div style={{ marginBottom: 8 }}>
              <Cta label={cd.comparison.cta} path={P("comparison", "cta")} href={checkoutUrl} testId="button-cold-drip-cmp-cta" />
            </div>
            <EditableText value={cd.comparison.ctaSubtext} path={P("comparison", "ctaSubtext")} as="p" style={{ fontSize: 12, color: MUTED, margin: 0 }} placeholder="CTA subtext" testId="text-cold-drip-cmp-cta-sub" regenBlockKey={rk("comparison")} />
          </div>
        </div>
      </Shell>
    ),
    inlineBuyCta: () => (
      <Shell k="inlineBuyCta" style={{ backgroundColor: CREAM, padding: "54px 0" }}>
        <div style={{ ...wrap, maxWidth: 720, textAlign: "center" }}>
          <Eyebrow value={cd.inlineBuyCta.eyebrow} path={P("inlineBuyCta", "eyebrow")} testId="text-cold-drip-inline-eyebrow" />
          <Heading value={cd.inlineBuyCta.heading} path={P("inlineBuyCta", "heading")} testId="text-cold-drip-inline-heading" regenKey={rk("inlineBuyCta")} size={32} />
          <EditableText value={cd.inlineBuyCta.body} path={P("inlineBuyCta", "body")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.65, color: MUTED, margin: "0 0 18px" }} placeholder="Body" testId="text-cold-drip-inline-body" regenBlockKey={rk("inlineBuyCta")} />
          <div style={{ display: "inline-block", textAlign: "left", marginBottom: 20 }}>
            {cd.inlineBuyCta.bullets.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                <CheckCircle2 className="h-4 w-4" style={{ color: TEAL, flexShrink: 0, marginTop: 3 }} />
                <EditableText value={b} path={P("inlineBuyCta", "bullets", i)} as="span" multiline style={{ fontSize: 14, color: INK }} placeholder="Bullet" testId={`text-cold-drip-inline-bullet-${i}`} regenBlockKey={rk("inlineBuyCta")} />
              </div>
            ))}
          </div>
          <div>
            <Cta label={cd.buyBox.ctaPrimary} path={P("buyBox", "ctaPrimary")} href={checkoutUrl} testId="button-cold-drip-inline-cta" />
          </div>
        </div>
      </Shell>
    ),
    ingredients: () => (
      <Shell k="ingredients" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 30px" }}>
            <Eyebrow value={cd.ingredients.eyebrow} path={P("ingredients", "eyebrow")} testId="text-cold-drip-ing-eyebrow" />
            <Heading value={cd.ingredients.heading} path={P("ingredients", "heading")} testId="text-cold-drip-ing-heading" regenKey={rk("ingredients")} size={34} />
            <EditableText value={cd.ingredients.intro} path={P("ingredients", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: 0 }} placeholder="Intro" testId="text-cold-drip-ing-intro" regenBlockKey={rk("ingredients")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18, marginBottom: 26 }} className="md:!grid-cols-2">
            {cd.ingredients.cards.map((c, i) => (
              <div key={i} style={{ backgroundColor: CREAM, borderRadius: 16, padding: 22 }} data-testid={`card-cold-drip-ing-${i}`}>
                <div style={{ height: 150, marginBottom: 14, borderRadius: 12, overflow: "hidden" }}>
                  <MediaSlot slot={`ingredient-${i}`} label={`${c.title || "Ingredient"} photo`} testId={`img-cold-drip-ing-${i}`} aiSlot={aiSlots[`ingredient-${i}`]} onRegenerate={COLD_DRIP_AI_SLOT_KEYS.has(`ingredient-${i}`) ? retrySlot(`ingredient-${i}`) : undefined} />
                </div>
                <EditableText value={c.title} path={P("ingredients", "cards", i, "title")} as="h3" style={{ fontSize: 15, fontWeight: 900, letterSpacing: "0.08em", color: WINE, margin: "0 0 6px" }} placeholder="Title" testId={`text-cold-drip-ing-title-${i}`} regenBlockKey={rk("ingredients")} />
                <EditableText value={c.lead} path={P("ingredients", "cards", i, "lead")} as="p" style={{ fontSize: 14.5, fontWeight: 700, color: INK, margin: "0 0 10px" }} placeholder="Lead" testId={`text-cold-drip-ing-lead-${i}`} regenBlockKey={rk("ingredients")} />
                {c.bullets.map((b, bi) => (
                  <div key={bi} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                    <CheckCircle2 className="h-4 w-4" style={{ color: TEAL, flexShrink: 0, marginTop: 2 }} />
                    <EditableText value={b} path={P("ingredients", "cards", i, "bullets", bi)} as="span" style={{ fontSize: 13.5, color: INK }} placeholder="Bullet" testId={`text-cold-drip-ing-bullet-${i}-${bi}`} regenBlockKey={rk("ingredients")} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: 8 }}>
              <Cta label={cd.ingredients.cta} path={P("ingredients", "cta")} href={checkoutUrl} testId="button-cold-drip-ing-cta" />
            </div>
            <EditableText value={cd.ingredients.ctaSubtext} path={P("ingredients", "ctaSubtext")} as="p" style={{ fontSize: 12, color: MUTED, margin: 0 }} placeholder="CTA subtext" testId="text-cold-drip-ing-cta-sub" regenBlockKey={rk("ingredients")} />
          </div>
        </div>
      </Shell>
    ),
    gaffleWorld: () => (
      <Shell k="gaffleWorld" style={{ backgroundColor: CREAM, padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 26px" }}>
            <Eyebrow value={cd.gaffleWorld.eyebrow} path={P("gaffleWorld", "eyebrow")} testId="text-cold-drip-world-eyebrow" />
            <Heading value={cd.gaffleWorld.heading} path={P("gaffleWorld", "heading")} testId="text-cold-drip-world-heading" regenKey={rk("gaffleWorld")} size={34} />
            <EditableText value={cd.gaffleWorld.intro} path={P("gaffleWorld", "intro")} as="p" style={{ fontSize: 15, color: MUTED, margin: 0 }} placeholder="Intro" testId="text-cold-drip-world-intro" regenBlockKey={rk("gaffleWorld")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 22 }} className="md:!grid-cols-4">
            {cd.gaffleWorld.tiktoks.map((t, i) => (
              <div key={i} style={{ borderRadius: 14, overflow: "hidden", backgroundColor: "#fff", border: `1px solid ${BORDER}` }} data-testid={`card-cold-drip-tiktok-${i}`}>
                <div style={{ aspectRatio: "9/13" }}>
                  <MediaSlot slot={`community-${i}`} label="Community photo/video still" testId={`img-cold-drip-community-${i}`} style={{ borderRadius: 0 }} aiSlot={aiSlots[`community-${i}`]} onRegenerate={retrySlot(`community-${i}`)} />
                </div>
                <EditableText value={t.caption} path={P("gaffleWorld", "tiktoks", i, "caption")} as="p" multiline style={{ fontSize: 12, lineHeight: 1.45, color: INK, margin: 0, padding: "9px 10px" }} placeholder="Caption" testId={`text-cold-drip-tiktok-${i}`} regenBlockKey={rk("gaffleWorld")} />
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            {editor ? (
              <span className="group/lplink relative inline-flex">
                <span style={{ fontSize: 14, fontWeight: 800, color: WINE, textDecoration: "underline" }}>
                  <EditableText value={cd.gaffleWorld.followCta} path={P("gaffleWorld", "followCta")} as="span" placeholder="Follow us" testId="text-cold-drip-world-follow" regenBlockKey={rk("gaffleWorld")} />
                </span>
                {editor.onEditLinkUrl && <LinkUrlPopover path={P("gaffleWorld", "followUrl")} url={cd.gaffleWorld.followUrl} testId="link-cold-drip-world-follow" />}
              </span>
            ) : (
              <a href={cd.gaffleWorld.followUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, fontWeight: 800, color: WINE, textDecoration: "underline" }} data-testid="link-cold-drip-world-follow">
                {cd.gaffleWorld.followCta}
              </a>
            )}
          </div>
        </div>
      </Shell>
    ),
    joinTheFam: () => (
      <Shell k="joinTheFam" style={{ backgroundColor: TEAL, padding: "50px 0" }}>
        <div style={{ ...wrap, maxWidth: 720, textAlign: "center" }}>
          <EditableText value={cd.joinTheFam.eyebrow} path={P("joinTheFam", "eyebrow")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: WINE, marginBottom: 10 }} placeholder="Eyebrow" testId="text-cold-drip-fam-eyebrow" regenBlockKey={rk("joinTheFam")} />
          <EditableText value={cd.joinTheFam.heading} path={P("joinTheFam", "heading")} as="h2" style={{ fontFamily: DISPLAY_FONT, fontSize: 34, fontWeight: 800, color: "#fff", margin: "0 0 10px" }} placeholder="Heading" testId="text-cold-drip-fam-heading" regenBlockKey={rk("joinTheFam")} regenAnchor />
          <EditableText value={cd.joinTheFam.body} path={P("joinTheFam", "body")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.65, color: "rgba(255,255,255,0.92)", margin: "0 0 16px" }} placeholder="Body" testId="text-cold-drip-fam-body" regenBlockKey={rk("joinTheFam")} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 20 }}>
            {cd.joinTheFam.chips.map((c, i) => (
              <EditableText key={i} value={c} path={P("joinTheFam", "chips", i)} as="span" style={{ fontSize: 12.5, fontWeight: 800, color: WINE, backgroundColor: "#fff", borderRadius: 999, padding: "6px 14px" }} placeholder="Chip" testId={`text-cold-drip-fam-chip-${i}`} regenBlockKey={rk("joinTheFam")} />
            ))}
          </div>
          <Cta label={cd.buyBox.ctaPrimary} path={P("buyBox", "ctaPrimary")} href={checkoutUrl} testId="button-cold-drip-fam-cta" />
        </div>
      </Shell>
    ),
    reviews: () => (
      <Shell k="reviews" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 28px" }}>
            <Eyebrow value={cd.reviews.eyebrow} path={P("reviews", "eyebrow")} testId="text-cold-drip-reviews-eyebrow" />
            <Heading value={cd.reviews.heading} path={P("reviews", "heading")} testId="text-cold-drip-reviews-heading" regenKey={rk("reviews")} size={34} />
            <EditableText value={cd.reviews.intro} path={P("reviews", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 0 10px" }} placeholder="Intro" testId="text-cold-drip-reviews-intro" regenBlockKey={rk("reviews")} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: INK }}>
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-4 w-4" style={{ color: GOLD, fill: GOLD }} />
              ))}
              <EditableText value={cd.reviews.ratingSummary} path={P("reviews", "ratingSummary")} as="span" placeholder="Rating summary" testId="text-cold-drip-reviews-summary" regenBlockKey={rk("reviews")} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }} className="md:!grid-cols-3">
            {cd.reviews.items.map((r, i) => (
              <div key={i} style={{ backgroundColor: CREAM, borderRadius: 16, padding: 20 }} data-testid={`card-cold-drip-review-${i}`}>
                <div style={{ display: "flex", gap: 3, marginBottom: 10 }}>
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-3.5 w-3.5" style={{ color: GOLD, fill: GOLD }} />
                  ))}
                </div>
                <EditableText value={r.title} path={P("reviews", "items", i, "title")} as="h3" style={{ fontSize: 14.5, fontWeight: 900, letterSpacing: "0.04em", color: WINE, margin: "0 0 8px" }} placeholder="Review title" testId={`text-cold-drip-review-title-${i}`} regenBlockKey={rk("reviews")} />
                <EditableText value={r.body} path={P("reviews", "items", i, "body")} as="p" multiline style={{ fontSize: 13.5, lineHeight: 1.6, color: INK, margin: "0 0 12px" }} placeholder="Review body" testId={`text-cold-drip-review-body-${i}`} regenBlockKey={rk("reviews")} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <EditableText value={r.name} path={P("reviews", "items", i, "name")} as="span" style={{ fontSize: 13, fontWeight: 800, color: INK }} placeholder="Name" testId={`text-cold-drip-review-name-${i}`} regenBlockKey={rk("reviews")} />
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: TEAL }}>
                    <BadgeCheck className="h-3.5 w-3.5" />
                    <EditableText value={r.badge} path={P("reviews", "items", i, "badge")} as="span" placeholder="Badge" testId={`text-cold-drip-review-badge-${i}`} regenBlockKey={rk("reviews")} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    whoWeAre: () => (
      <Shell k="whoWeAre" style={{ backgroundColor: CREAM, padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 30px" }}>
            <Eyebrow value={cd.whoWeAre.eyebrow} path={P("whoWeAre", "eyebrow")} testId="text-cold-drip-who-eyebrow" />
            <Heading value={cd.whoWeAre.heading} path={P("whoWeAre", "heading")} testId="text-cold-drip-who-heading" regenKey={rk("whoWeAre")} size={34} />
            <EditableText value={cd.whoWeAre.intro} path={P("whoWeAre", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: 0 }} placeholder="Intro" testId="text-cold-drip-who-intro" regenBlockKey={rk("whoWeAre")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20 }} className="md:!grid-cols-2">
            {([
              ["behindTheDrip", cd.whoWeAre.behindTheDrip, "story"],
              ["skincare", cd.whoWeAre.skincare, "skincare"],
            ] as const).map(([key, blk, slot], i) => (
              <div key={key} style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 18, overflow: "hidden" }} data-testid={`card-cold-drip-who-${key}`}>
                <div style={{ height: 190 }}>
                  <MediaSlot slot={slot} label={`${blk.title || "Story"} photo`} testId={`img-cold-drip-who-${i}`} style={{ borderRadius: 0 }} aiSlot={aiSlots[slot]} onRegenerate={retrySlot(slot)} />
                </div>
                <div style={{ padding: 22 }}>
                  {"eyebrow" in blk && (
                    <EditableText value={(blk as any).eyebrow} path={P("whoWeAre", key, "eyebrow")} as="div" style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", color: TEAL, marginBottom: 6 }} placeholder="Eyebrow" testId={`text-cold-drip-who-eyebrow-${i}`} regenBlockKey={rk("whoWeAre")} />
                  )}
                  <EditableText value={blk.title} path={P("whoWeAre", key, "title")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 22, fontWeight: 800, color: INK, margin: "0 0 10px" }} placeholder="Title" testId={`text-cold-drip-who-title-${i}`} regenBlockKey={rk("whoWeAre")} />
                  <EditableText value={blk.body1} path={P("whoWeAre", key, "body1")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: INK, margin: "0 0 10px" }} placeholder="Body" testId={`text-cold-drip-who-body1-${i}`} regenBlockKey={rk("whoWeAre")} />
                  <EditableText value={blk.body2} path={P("whoWeAre", key, "body2")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, margin: "0 0 14px" }} placeholder="Body" testId={`text-cold-drip-who-body2-${i}`} regenBlockKey={rk("whoWeAre")} />
                  {editor ? (
                    <span className="group/lplink relative inline-flex">
                      <span style={{ fontSize: 13.5, fontWeight: 800, color: WINE, textDecoration: "underline" }}>
                        <EditableText value={blk.cta} path={P("whoWeAre", key, "cta")} as="span" placeholder="CTA" testId={`text-cold-drip-who-cta-${i}`} regenBlockKey={rk("whoWeAre")} />
                      </span>
                      {editor.onEditLinkUrl && <LinkUrlPopover path={P("whoWeAre", key, "ctaUrl")} url={blk.ctaUrl} testId={`link-cold-drip-who-cta-${i}`} />}
                    </span>
                  ) : (
                    <a href={blk.ctaUrl || undefined} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, fontWeight: 800, color: WINE, textDecoration: "underline" }} data-testid={`link-cold-drip-who-cta-${i}`}>
                      {blk.cta}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    visit: () => (
      <Shell k="visit" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={{ ...wrap, display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="lg:!grid-cols-2">
          <div>
            <EditableText value={cd.visit.badge} path={P("visit", "badge")} as="div" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", color: WINE, backgroundColor: CREAM, borderRadius: 999, padding: "6px 14px", marginBottom: 14 }} placeholder="Badge" testId="text-cold-drip-visit-badge" regenBlockKey={rk("visit")} />
            <Heading value={cd.visit.heading} path={P("visit", "heading")} testId="text-cold-drip-visit-heading" regenKey={rk("visit")} size={34} />
            <EditableText value={cd.visit.intro} path={P("visit", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.65, color: MUTED, margin: "0 0 20px" }} placeholder="Intro" testId="text-cold-drip-visit-intro" regenBlockKey={rk("visit")} />
            <div style={{ display: "flex", gap: 22, marginBottom: 22 }}>
              {cd.visit.stats.map((s, i) => (
                <div key={i} data-testid={`stat-cold-drip-visit-${i}`}>
                  <EditableText value={s.num} path={P("visit", "stats", i, "num")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 26, fontWeight: 900, color: RED }} placeholder="Stat" testId={`text-cold-drip-visit-stat-${i}`} regenBlockKey={rk("visit")} />
                  <EditableText value={s.label} path={P("visit", "stats", i, "label")} as="div" style={{ fontSize: 12, color: MUTED }} placeholder="Label" testId={`text-cold-drip-visit-stat-label-${i}`} regenBlockKey={rk("visit")} />
                </div>
              ))}
            </div>
            {/* Contacts: hidden on the PUBLIC page when the values are blank
                (the sanitizer blanks unknown brand facts) — in the editor they
                stay visible as editable placeholders. */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
              {(editor || cd.visit.contacts.phone || cd.visit.contacts.phoneLabel) && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, color: INK }}>
                  <Phone className="h-4 w-4" style={{ color: TEAL }} />
                  <EditableText value={cd.visit.contacts.phoneLabel} path={P("visit", "contacts", "phoneLabel")} as="span" style={{ fontWeight: 700, color: MUTED }} placeholder="Label" testId="text-cold-drip-visit-phone-label" regenBlockKey={rk("visit")} />
                  <EditableText value={cd.visit.contacts.phone} path={P("visit", "contacts", "phone")} as="span" style={{ fontWeight: 700 }} placeholder="Phone" testId="text-cold-drip-visit-phone" regenBlockKey={rk("visit")} />
                </span>
              )}
              {(editor || cd.visit.contacts.email || cd.visit.contacts.emailLabel) && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, color: INK }}>
                  <EditableText value={cd.visit.contacts.emailLabel} path={P("visit", "contacts", "emailLabel")} as="span" style={{ fontWeight: 700, color: MUTED }} placeholder="Label" testId="text-cold-drip-visit-email-label" regenBlockKey={rk("visit")} />
                  <EditableText value={cd.visit.contacts.email} path={P("visit", "contacts", "email")} as="span" style={{ fontWeight: 700 }} placeholder="Email" testId="text-cold-drip-visit-email" regenBlockKey={rk("visit")} />
                </span>
              )}
            </div>
          </div>
          {/* Door sign card */}
          <div style={{ backgroundColor: CREAM, border: `1px solid ${BORDER}`, borderRadius: 20, padding: 24, alignSelf: "start" }} data-testid="card-cold-drip-doorsign">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <EditableText value={cd.visit.doorSign.header} path={P("visit", "doorSign", "header")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 20, fontWeight: 800, color: INK }} placeholder="Header" testId="text-cold-drip-door-header" regenBlockKey={rk("visit")} />
              <EditableText value={cd.visit.doorSign.openBanner} path={P("visit", "doorSign", "openBanner")} as="span" style={{ fontSize: 11.5, fontWeight: 800, color: "#fff", backgroundColor: TEAL, borderRadius: 999, padding: "4px 12px" }} placeholder="Open" testId="text-cold-drip-door-open" regenBlockKey={rk("visit")} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 800, letterSpacing: "0.06em", color: MUTED, marginBottom: 8 }}>
              <Clock className="h-3.5 w-3.5" />
              <EditableText value={cd.visit.doorSign.hoursLabel} path={P("visit", "doorSign", "hoursLabel")} as="span" placeholder="Hours" testId="text-cold-drip-door-hours-label" regenBlockKey={rk("visit")} />
            </div>
            <div style={{ marginBottom: 14 }}>
              {cd.visit.doorSign.hours.map((h, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, borderBottom: i < cd.visit.doorSign.hours.length - 1 ? `1px solid ${BORDER}` : "none" }} data-testid={`row-cold-drip-hours-${i}`}>
                  <EditableText value={h.day} path={P("visit", "doorSign", "hours", i, "day")} as="span" style={{ fontWeight: 700, color: INK }} placeholder="Day" testId={`text-cold-drip-hours-day-${i}`} regenBlockKey={rk("visit")} />
                  <EditableText value={h.hours} path={P("visit", "doorSign", "hours", i, "hours")} as="span" style={{ color: MUTED }} placeholder="Hours" testId={`text-cold-drip-hours-val-${i}`} regenBlockKey={rk("visit")} />
                </div>
              ))}
            </div>
            <EditableText value={cd.visit.doorSign.addressCallout} path={P("visit", "doorSign", "addressCallout")} as="p" style={{ fontSize: 13.5, fontWeight: 700, color: WINE, margin: "0 0 6px" }} placeholder="Callout" testId="text-cold-drip-door-callout" regenBlockKey={rk("visit")} />
            {/* Address + maps: hidden on the PUBLIC page when blank (unknown
                brand facts are blanked by the sanitizer, never invented). */}
            {(editor || cd.visit.doorSign.address) && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10 }}>
                <MapPin className="h-4 w-4" style={{ color: RED, flexShrink: 0, marginTop: 2 }} />
                <EditableText value={cd.visit.doorSign.address} path={P("visit", "doorSign", "address")} as="span" style={{ fontSize: 13.5, color: INK }} placeholder="Address" testId="text-cold-drip-door-address" regenBlockKey={rk("visit")} />
              </div>
            )}
            {editor ? (
              <span className="group/lplink relative inline-flex">
                <span style={{ fontSize: 13, fontWeight: 800, color: TEAL, textDecoration: "underline" }}>
                  <EditableText value={cd.visit.doorSign.mapsCta} path={P("visit", "doorSign", "mapsCta")} as="span" placeholder="Maps" testId="text-cold-drip-door-maps" regenBlockKey={rk("visit")} />
                </span>
                {editor.onEditLinkUrl && <LinkUrlPopover path={P("visit", "doorSign", "mapsUrl")} url={cd.visit.doorSign.mapsUrl} testId="link-cold-drip-door-maps" />}
              </span>
            ) : cd.visit.doorSign.mapsUrl && cd.visit.doorSign.mapsCta ? (
              <a href={cd.visit.doorSign.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 800, color: TEAL, textDecoration: "underline" }} data-testid="link-cold-drip-door-maps">
                {cd.visit.doorSign.mapsCta}
              </a>
            ) : null}
          </div>
        </div>
      </Shell>
    ),
    ctaStrip: () => (
      <Shell k="ctaStrip" style={{ backgroundColor: RED, padding: "44px 0" }}>
        <div style={{ ...wrap, maxWidth: 680, textAlign: "center" }}>
          <EditableText value={cd.ctaStrip.heading} path={P("ctaStrip", "heading")} as="h2" style={{ fontFamily: DISPLAY_FONT, fontSize: 34, fontWeight: 900, color: "#fff", margin: "0 0 10px" }} placeholder="Heading" testId="text-cold-drip-strip-heading" regenBlockKey={rk("ctaStrip")} regenAnchor />
          <EditableText value={cd.ctaStrip.body} path={P("ctaStrip", "body")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(255,255,255,0.9)", margin: "0 0 18px" }} placeholder="Body" testId="text-cold-drip-strip-body" regenBlockKey={rk("ctaStrip")} />
          <Cta label={cd.ctaStrip.cta} path={P("ctaStrip", "cta")} href={checkoutUrl} testId="button-cold-drip-strip-cta" variant="teal" />
        </div>
      </Shell>
    ),
    faq: () => (
      <Shell k="faq" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <div style={{ textAlign: "center", marginBottom: 26 }}>
            <Eyebrow value={cd.faq.eyebrow} path={P("faq", "eyebrow")} testId="text-cold-drip-faq-eyebrow" />
            <Heading value={cd.faq.heading} path={P("faq", "heading")} testId="text-cold-drip-faq-heading" regenKey={rk("faq")} size={32} />
          </div>
          {cd.faq.items.map((f, i) => (
            <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 18px", marginBottom: 12 }} data-testid={`card-cold-drip-faq-${i}`}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <Minus className="h-4 w-4" style={{ color: RED, flexShrink: 0, marginTop: 3 }} />
                <EditableText value={f.q} path={P("faq", "items", i, "q")} as="h3" style={{ fontSize: 15.5, fontWeight: 800, color: INK, margin: 0 }} placeholder="Question" testId={`text-cold-drip-faq-q-${i}`} regenBlockKey={rk("faq")} />
              </div>
              <EditableText value={f.a} path={P("faq", "items", i, "a")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, margin: 0, paddingLeft: 26 }} placeholder="Answer" testId={`text-cold-drip-faq-a-${i}`} regenBlockKey={rk("faq")} />
            </div>
          ))}
        </div>
      </Shell>
    ),
    footer: () => (
      <Shell k="footer" style={{ backgroundColor: INK, padding: "44px 0 90px" }}>
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={{ width: 120, height: 40, margin: "0 auto 14px" }}>
            <MediaSlot slot="footer-logo" label="Footer logo" testId="img-cold-drip-footer-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 40, borderRadius: 6 }} />
          </div>
          <EditableText value={cd.footer.tagline} path={P("footer", "tagline")} as="p" style={{ fontSize: 13.5, color: "rgba(255,255,255,0.7)", margin: "0 auto 16px", maxWidth: 480 }} placeholder="Tagline" testId="text-cold-drip-footer-tagline" regenBlockKey={rk("footer")} regenAnchor />
          <div style={{ display: "flex", justifyContent: "center", gap: 22, marginBottom: 16 }}>
            {cd.footer.links.map((l, i) =>
              editor ? (
                <span key={i} className="group/lplink relative inline-flex">
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", textDecoration: "underline" }}>
                    <EditableText value={l.label} path={P("footer", "links", i, "label")} as="span" placeholder="Link" testId={`text-cold-drip-footer-link-${i}`} regenBlockKey={rk("footer")} />
                  </span>
                  {editor.onEditLinkUrl && <LinkUrlPopover path={P("footer", "links", i, "href")} url={l.href} testId={`link-cold-drip-footer-${i}`} />}
                </span>
              ) : (
                <a key={i} href={l.href || undefined} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 700, color: "#fff", textDecoration: "underline" }} data-testid={`link-cold-drip-footer-${i}`}>
                  {l.label}
                </a>
              ),
            )}
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 6 }} data-testid="text-cold-drip-footer-copyright">
            {cd.footer.copyright.replace("{year}", String(new Date().getFullYear()))}
          </div>
          <EditableText value={cd.footer.signoff} path={P("footer", "signoff")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 15, fontWeight: 800, color: TEAL }} placeholder="Signoff" testId="text-cold-drip-footer-signoff" regenBlockKey={rk("footer")} />
        </div>
      </Shell>
    ),
    stickyBar: () => (
      // Editor/preview: render inline (as the page's last section) so the bar
      // stays inside the preview canvas instead of position:fixed escaping to
      // the app viewport and covering the builder chrome (same pattern as the
      // product-hero sticky bar). Public page: pin it to the bottom.
      <Shell k="stickyBar" style={editor
        ? { backgroundColor: "#fff", borderTop: `1px solid ${BORDER}`, boxShadow: "0 -6px 18px rgba(0,0,0,0.08)" }
        : { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, backgroundColor: "#fff", borderTop: `1px solid ${BORDER}`, boxShadow: "0 -6px 18px rgba(0,0,0,0.08)" }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 20px" }}>
          <span style={{ fontSize: 13.5, fontWeight: 800, color: INK, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            <span data-testid="text-cold-drip-sticky-price">{money(price)}</span>
            {full > price && <span style={{ fontSize: 12, color: MUTED, textDecoration: "line-through" }}>{money(full)}</span>}
            <EditableText value={cd.stickyBar.label} path={P("stickyBar", "label")} as="span" style={{ fontWeight: 700, color: WINE }} placeholder="Sticky label" testId="text-cold-drip-sticky-label" regenBlockKey={rk("stickyBar")} regenAnchor />
          </span>
          <Cta label={cd.stickyBar.cta} path={P("stickyBar", "cta")} href={checkoutUrl} testId="button-cold-drip-sticky-cta" />
        </div>
      </Shell>
    ),
  };

  return (
    <div style={{ backgroundColor: CREAM, minHeight: "100vh", fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif' }} data-testid="cold-drip-page">
      <LpReorderGroup
        groupKey="coldDrip:sections"
        items={LP_COLD_DRIP_SECTIONS.map((s) => ({
          key: `cold-drip-${s.key}`,
          node: renderers[s.key] ? <div key={`cold-drip-${s.key}`}>{renderers[s.key]()}</div> : null,
        })).filter((it) => it.node)}
      />
    </div>
  );
}
